
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { motion } from "framer-motion";
import { ProfileHeader } from "@/components/cook-profile/ProfileHeader";
import { SocialMediaLinks } from "@/components/cook-profile/SocialMediaLinks";
import { MealsList } from "@/components/cook-profile/MealsList";
import { ReviewsList } from "@/components/cook-profile/ReviewsList";
import { Badge } from "@/components/ui/badge";
import { KitchenGallery } from "@/components/cook-profile/KitchenGallery";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const CookProfile = () => {
  const { id } = useParams();

  const { data: cook, isLoading: isLoadingCook } = useQuery({
    queryKey: ['cook', id],
    queryFn: async () => {
      // First try to get the profile and all verification data
      const { data, error } = await supabase
        .from('profiles')
        .select(`
          *,
          cook_verifications (
            id,
            status,
            show_kitchen_photos,
            public_kitchen_photos
          )
        `)
        .eq('id', id)
        .single();

      if (error) throw error;
      console.log("Cook data:", data);
      return data;
    },
    enabled: !!id
  });

  const { data: meals = [], isLoading: isLoadingMeals } = useQuery({
    queryKey: ['cook-meals', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('meals')
        .select('*')
        .eq('chef_id', id)
        .eq('status', 'active');

      if (error) throw error;
      return data;
    },
    enabled: !!id
  });

  const { data: reviews = [], isLoading: isLoadingReviews } = useQuery({
    queryKey: ['cook-reviews', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('reviews')
        .select(`
          *,
          user:profiles!reviews_user_id_fkey (
            first_name,
            last_name
          ),
          meal:meals!reviews_meal_id_fkey (
            name,
            chef_id
          )
        `)
        .eq('meal.chef_id', id);

      if (error) throw error;
      
      const filteredReviews = data.filter(review => review.meal?.chef_id === id);
      
      return filteredReviews;
    },
    enabled: !!id
  });

  if (isLoadingCook || isLoadingMeals || isLoadingReviews) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!cook) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-lg">Cook not found</p>
      </div>
    );
  }

  // Handle the case where cook_verifications is an object instead of an array
  let verificationData = null;
  let kitchenPhotos = [];
  let isCertified = false;
  let showKitchenPhotos = false;

  // Check if cook_verifications exists and handle both object and array cases
  if (cook.cook_verifications) {
    // If it's a single object (not in an array)
    if (!Array.isArray(cook.cook_verifications)) {
      verificationData = cook.cook_verifications;
    } 
    // If it's an array with at least one item
    else if (cook.cook_verifications.length > 0) {
      verificationData = cook.cook_verifications[0];
    }
  }

  // Now determine certification status and kitchen photos using verificationData
  if (verificationData) {
    isCertified = verificationData.status === 'approved';
    
    // Safely access public_kitchen_photos
    if (Array.isArray(verificationData.public_kitchen_photos)) {
      kitchenPhotos = verificationData.public_kitchen_photos;
    }
    
    // Determine if kitchen photos should be shown
    showKitchenPhotos = 
      isCertified && 
      verificationData.show_kitchen_photos === true && 
      kitchenPhotos.length > 0;
  }
  
  console.log("Verification data:", verificationData);
  console.log("Is certified:", isCertified);
  console.log("Kitchen photos:", kitchenPhotos);
  console.log("Show kitchen photos:", showKitchenPhotos);

  const renderBadgeList = (items: string[] | null, label: string) => {
    if (!items || items.length === 0) return null;
    
    return (
      <div className="mt-4">
        <h3 className="text-sm font-medium mb-2">{label}</h3>
        <div className="flex flex-wrap gap-2">
          {items.map((item, index) => (
            <Badge key={index} variant="outline" className="bg-primary/5">
              {item}
            </Badge>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background pb-16">
      <div className="container mx-auto px-4 py-8">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-8"
        >
          <ProfileHeader
            imageUrl={cook.image_url}
            firstName={cook.first_name}
            lastName={cook.last_name}
            city={cook.city}
            state={cook.state}
            isCertified={isCertified}
          />

          <SocialMediaLinks
            instagramHandle={cook.instagram_handle}
            facebookHandle={cook.facebook_handle}
            tiktokHandle={cook.tiktok_handle}
            pinterestHandle={cook.pinterest_handle}
            onSocialMediaClick={() => {}}
          />

          <div className="space-y-4">
            {cook.cooking_specialties && (
              <div>
                <h3 className="text-sm font-medium mb-2">Specialties</h3>
                <p className="text-muted-foreground">{cook.cooking_specialties}</p>
              </div>
            )}

            {cook.years_experience && (
              <div>
                <h3 className="text-sm font-medium mb-2">Experience</h3>
                <p className="text-muted-foreground">
                  {cook.years_experience} years of cooking experience
                  {cook.other_experience && ` • ${cook.other_experience}`}
                </p>
              </div>
            )}

            {renderBadgeList(cook.prep_methods, "Preparation Methods")}
            {renderBadgeList(cook.kitchen_notes, "Kitchen Notes")}
            {renderBadgeList(cook.experience_types, "Experience Types")}
          </div>

          <Tabs defaultValue="meals" className="w-full">
            <TabsList className={`grid w-full ${showKitchenPhotos ? 'grid-cols-3' : 'grid-cols-2'}`}>
              <TabsTrigger value="meals">Active Meals</TabsTrigger>
              <TabsTrigger value="reviews">Reviews</TabsTrigger>
              {showKitchenPhotos && (
                <TabsTrigger value="kitchen">Kitchen Photos</TabsTrigger>
              )}
            </TabsList>
            
            <TabsContent value="meals" className="mt-6">
              <MealsList
                meals={meals}
                chefName={`${cook.first_name} ${cook.last_name}`}
              />
            </TabsContent>
            
            <TabsContent value="reviews" className="mt-6">
              <ReviewsList reviews={reviews} />
            </TabsContent>
            
            {showKitchenPhotos && (
              <TabsContent value="kitchen" className="mt-6">
                <KitchenGallery 
                  photos={kitchenPhotos}
                  isVisible={true}
                  chefName={`${cook.first_name}`}
                />
              </TabsContent>
            )}
          </Tabs>
        </motion.div>
      </div>
    </div>
  );
};

export default CookProfile;
